import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtMoney } from "@/lib/api";
import { PageHeader, Section, Pill } from "@/components/ui-kit";
import { toast } from "sonner";

export default function PlansPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/plans").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const blank = { name: "", description: "", monthly_price: 0, annual_price: 0, monthly_stripe_price_id: "", annual_stripe_price_id: "", features: [], max_customers: 100, max_users: 5, is_active: true };

  const startEdit = (p) => setEditing(p ? { ...p, features_text: (p.features || []).join("\n") } : { ...blank, features_text: "" });

  const save = async (e) => {
    e.preventDefault();
    try {
      const features = (editing.features_text || "").split("\n").map((s) => s.trim()).filter(Boolean);
      const payload = { ...editing, features, monthly_price: Number(editing.monthly_price), annual_price: Number(editing.annual_price), max_customers: Number(editing.max_customers), max_users: Number(editing.max_users) };
      delete payload.features_text;
      delete payload.id; delete payload.created_at;
      if (editing.id) await api.put(`/admin/plans/${editing.id}`, payload);
      else await api.post("/admin/plans", payload);
      toast.success("Plan saved");
      setEditing(null); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const inputCls = "w-full bg-white border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm";

  return (
    <div data-testid="plans-page">
      <PageHeader title="Subscription Plans" subtitle="Global Admin · Plans"
        actions={<button onClick={() => startEdit(null)} className="btn-primary" data-testid="new-plan-btn">New Plan</button>}
      />

      {editing && (
        <Section title={editing.id ? "Edit plan" : "New plan"} testId="plan-form" actions={<button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>}>
          <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
            <div><label className="label block mb-1">Name</label><input required className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="plan-name" /></div>
            <div><label className="label block mb-1">Active</label><select className={inputCls} value={String(editing.is_active)} onChange={(e) => setEditing({ ...editing, is_active: e.target.value === "true" })} data-testid="plan-active"><option value="true">Active</option><option value="false">Inactive</option></select></div>
            <div className="md:col-span-2"><label className="label block mb-1">Description</label><input className={inputCls} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="plan-description" /></div>
            <div><label className="label block mb-1">Monthly Price</label><input type="number" step="0.01" className={inputCls} value={editing.monthly_price} onChange={(e) => setEditing({ ...editing, monthly_price: e.target.value })} data-testid="plan-monthly-price" /></div>
            <div><label className="label block mb-1">Annual Price</label><input type="number" step="0.01" className={inputCls} value={editing.annual_price} onChange={(e) => setEditing({ ...editing, annual_price: e.target.value })} data-testid="plan-annual-price" /></div>
            <div><label className="label block mb-1">Monthly Stripe Price ID</label><input className={inputCls} value={editing.monthly_stripe_price_id || ""} onChange={(e) => setEditing({ ...editing, monthly_stripe_price_id: e.target.value })} data-testid="plan-monthly-price-id" /></div>
            <div><label className="label block mb-1">Annual Stripe Price ID</label><input className={inputCls} value={editing.annual_stripe_price_id || ""} onChange={(e) => setEditing({ ...editing, annual_stripe_price_id: e.target.value })} data-testid="plan-annual-price-id" /></div>
            <div><label className="label block mb-1">Max Customers</label><input type="number" className={inputCls} value={editing.max_customers} onChange={(e) => setEditing({ ...editing, max_customers: e.target.value })} data-testid="plan-max-customers" /></div>
            <div><label className="label block mb-1">Max Users</label><input type="number" className={inputCls} value={editing.max_users} onChange={(e) => setEditing({ ...editing, max_users: e.target.value })} data-testid="plan-max-users" /></div>
            <div className="md:col-span-2"><label className="label block mb-1">Features (one per line)</label><textarea className={inputCls + " min-h-[100px]"} value={editing.features_text} onChange={(e) => setEditing({ ...editing, features_text: e.target.value })} data-testid="plan-features" /></div>
            <div className="md:col-span-2 flex justify-end"><button type="submit" className="btn-primary" data-testid="plan-submit-btn">Save plan</button></div>
          </form>
        </Section>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {items.map((p) => (
          <div key={p.id} className="card-flat p-6" data-testid={`plan-card-${p.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="label">{p.is_active ? "Active" : "Inactive"}</div>
                <h3 className="font-display text-2xl font-semibold tracking-tight mt-1">{p.name}</h3>
              </div>
              <Pill variant="info">{fmtMoney(p.monthly_price)}/mo</Pill>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{p.description}</p>
            <ul className="text-sm space-y-1.5 mb-5">
              {p.features?.map((f, i) => <li key={i} className="text-foreground">• {f}</li>)}
            </ul>
            <div className="text-xs text-muted-foreground mb-4">Annual: {fmtMoney(p.annual_price)}</div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(p)} className="btn-ghost flex-1" data-testid={`edit-plan-${p.id}`}>Edit</button>
              <button onClick={async () => {
                const hard = window.confirm(`Delete plan "${p.name}"?\n\nOK = Permanently delete (only if no active subscription)\nCancel = Mark inactive`);
                try {
                  if (hard) await api.delete(`/admin/plans/${p.id}/hard`);
                  else await api.delete(`/admin/plans/${p.id}`);
                  toast.success(hard ? "Plan permanently deleted" : "Plan marked inactive");
                  load();
                } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
              }} className="btn-ghost text-secondary" data-testid={`delete-plan-${p.id}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
